import {
    AlignLeft,
    Calendar,
    Clock,
    DollarSign,
    Globe,
    Globe2,
    Hash,
    List,
    ListChecks,
    ListOrdered,
    Mail,
    Phone,
    Percent,
    Search,
    Star,
    ToggleLeft,
    Type,
    User,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ModuleFieldType } from './types';

/** TanStack Query key factory for the module builder. */
export const MODULE_BUILDER_QUERY_KEYS = {
    all: ['module-builder'] as const,
    modules: () => ['module-builder', 'modules'] as const,
    module: (moduleId: string) => ['module-builder', 'modules', moduleId] as const,
    records: (moduleId: string, params?: unknown) =>
        ['module-builder', 'records', moduleId, params ?? null] as const,
    /** Prefix for invalidating every records query of a module. */
    recordsPrefix: (moduleId: string) => ['module-builder', 'records', moduleId] as const,
    record: (moduleId: string, recordId: string) =>
        ['module-builder', 'record', moduleId, recordId] as const,
    contactCustomFields: (contactId: string) =>
        ['module-builder', 'contact-custom-fields', contactId] as const,
};

export interface FieldTypeMeta {
    label: string;
    description: string;
    icon: LucideIcon;
    /** Accent color (hex) used for the icon chip — matches omnichannel. */
    color: string;
}

/** Display metadata for every field type (label, description, icon, color). */
export const FIELD_TYPE_META: Record<ModuleFieldType, FieldTypeMeta> = {
    TEXT: { label: 'Text', description: 'Single line of text', icon: Type, color: '#3B82F6' },
    TEXTAREA: {
        label: 'Long text',
        description: 'Multi-line paragraph text',
        icon: AlignLeft,
        color: '#3B82F6',
    },
    NUMBER: { label: 'Number', description: 'Numeric value', icon: Hash, color: '#10B981' },
    CURRENCY: {
        label: 'Currency',
        description: 'Monetary amount',
        icon: DollarSign,
        color: '#10B981',
    },
    PERCENTAGE: {
        label: 'Percentage',
        description: 'Percentage value',
        icon: Percent,
        color: '#10B981',
    },
    DATE: { label: 'Date', description: 'Calendar date', icon: Calendar, color: '#F59E0B' },
    DATETIME: {
        label: 'Date & time',
        description: 'Date with time',
        icon: Clock,
        color: '#F59E0B',
    },
    BOOLEAN: {
        label: 'Checkbox',
        description: 'True / false toggle',
        icon: ToggleLeft,
        color: '#8B5CF6',
    },
    SELECT: { label: 'Dropdown', description: 'Choose one option', icon: List, color: '#EC4899' },
    MULTI_SELECT: {
        label: 'Multi-select',
        description: 'Choose multiple options',
        icon: ListChecks,
        color: '#F97316',
    },
    URL: { label: 'URL', description: 'Web link', icon: Globe, color: '#06B6D4' },
    EMAIL: { label: 'Email', description: 'Email address', icon: Mail, color: '#EF4444' },
    PHONE: { label: 'Phone', description: 'Phone number', icon: Phone, color: '#14B8A6' },
    LOOKUP: {
        label: 'Lookup',
        description: 'Link to another record',
        icon: Search,
        color: '#6366F1',
    },
    USER: { label: 'User', description: 'Link to a team member', icon: User, color: '#8B5CF6' },
    RATING: { label: 'Rating', description: 'Star rating', icon: Star, color: '#EAB308' },
    AUTO_INCREMENT: {
        label: 'Auto number',
        description: 'Auto-incrementing number',
        icon: ListOrdered,
        color: '#6B7280',
    },
    TIMEZONE: { label: 'Timezone', description: 'IANA timezone', icon: Globe2, color: '#06B6D4' },
};

/** Field types in display order for the type picker. */
const FIELD_TYPE_ORDER: ModuleFieldType[] = [
    'TEXT',
    'TEXTAREA',
    'NUMBER',
    'CURRENCY',
    'PERCENTAGE',
    'DATE',
    'DATETIME',
    'BOOLEAN',
    'SELECT',
    'MULTI_SELECT',
    'URL',
    'EMAIL',
    'PHONE',
    'LOOKUP',
    'USER',
    'RATING',
    'AUTO_INCREMENT',
    'TIMEZONE',
];

export const FIELD_TYPE_OPTIONS = FIELD_TYPE_ORDER.map((value) => ({
    value,
    ...FIELD_TYPE_META[value],
}));

/** Field types that store a list of options. */
const OPTION_FIELD_TYPES: ReadonlySet<ModuleFieldType> = new Set(['SELECT', 'MULTI_SELECT']);
/** Field types that cannot have a default value (mirrors the backend). */
const NO_DEFAULT_FIELD_TYPES: ReadonlySet<ModuleFieldType> = new Set([
    'LOOKUP',
    'USER',
    'AUTO_INCREMENT',
    'MULTI_SELECT',
]);

export function fieldSupportsOptions(type: ModuleFieldType): boolean {
    return OPTION_FIELD_TYPES.has(type);
}

export function fieldSupportsDefault(type: ModuleFieldType): boolean {
    return !NO_DEFAULT_FIELD_TYPES.has(type);
}

// ── Validation rules ────────────────────────────────────────────────────────

/** Field types validated by min/max character length + regex pattern. */
export const LENGTH_RULE_FIELD_TYPES: ReadonlySet<ModuleFieldType> = new Set([
    'TEXT',
    'TEXTAREA',
    'URL',
    'EMAIL',
    'PHONE',
]);
/** Field types validated by numeric min/max. */
export const NUMERIC_RULE_FIELD_TYPES: ReadonlySet<ModuleFieldType> = new Set([
    'NUMBER',
    'CURRENCY',
    'PERCENTAGE',
    'RATING',
]);
/** Field types validated by date min/max. */
export const DATE_RULE_FIELD_TYPES: ReadonlySet<ModuleFieldType> = new Set(['DATE', 'DATETIME']);

export function fieldSupportsValidationRules(type: ModuleFieldType): boolean {
    return (
        LENGTH_RULE_FIELD_TYPES.has(type) ||
        NUMERIC_RULE_FIELD_TYPES.has(type) ||
        DATE_RULE_FIELD_TYPES.has(type) ||
        type === 'MULTI_SELECT'
    );
}
